import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { api, apiError } from "@/lib/api";
import { formatEUR } from "@/lib/format";
import { toast } from "sonner";
import { Trash2, ArrowRight } from "lucide-react";

export function Cart() {
  const { items, remove, setQty, subtotal, count } = useCart();
  return (
    <div className="anim-in container-x py-14">
      <p className="tech-label mb-3">Carrinho · {count} artigo(s)</p>
      <h1 className="text-4xl font-bold tracking-tighter">Rever encomenda</h1>
      {items.length === 0 ? (
        <div className="mt-10 border border-border p-10 text-center">
          <p className="tech-label">carrinho vazio</p>
          <Link to="/loja" className="btn-primary mt-4">Ver produtos <ArrowRight size={14} /></Link>
        </div>
      ) : (
        <div className="mt-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-3">
            {items.map((it) => {
              let unit = it.unit_price;
              Object.values(it.customizations || {}).forEach((c) => { if (c?.price_add) unit += c.price_add; });
              return (
                <div key={it._key} className="flex gap-4 border border-border p-4 items-center bg-card">
                  <div className="w-20 h-20 bg-secondary shrink-0 overflow-hidden">
                    {it.image && <img src={it.image} className="w-full h-full object-cover" alt={it.name} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="mono text-[10px] uppercase text-muted-foreground">{it.sku}</div>
                    <div className="font-medium">{it.name}</div>
                    <div className="mono text-[10px] text-muted-foreground mt-1">
                      {Object.entries(it.variant || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                      {Object.entries(it.customizations || {}).map(([k, v]) => v?.value ? ` · ${v.label}: ${v.value}` : "").join("")}
                    </div>
                  </div>
                  <div className="flex items-center border border-border">
                    <button onClick={() => setQty(it._key, it.quantity - 1)} className="px-2 py-1">−</button>
                    <span className="w-8 text-center mono text-sm">{it.quantity}</span>
                    <button onClick={() => setQty(it._key, it.quantity + 1)} className="px-2 py-1">+</button>
                  </div>
                  <div className="w-24 text-right font-semibold">{formatEUR(unit * it.quantity)}</div>
                  <button onClick={() => remove(it._key)} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>
                </div>
              );
            })}
          </div>
          <div className="border border-border p-6 h-fit sticky top-20 bg-card space-y-3">
            <div className="flex justify-between text-sm"><span className="tech-label">Subtotal</span><span className="font-medium">{formatEUR(subtotal)}</span></div>
            <div className="flex justify-between text-sm"><span className="tech-label">Portes</span><span className="text-muted-foreground text-xs">no checkout</span></div>
            <div className="flex justify-between text-sm"><span className="tech-label">IVA</span><span className="text-muted-foreground text-xs">23% incluído no checkout</span></div>
            <Link to="/checkout" className="btn-primary w-full justify-center mt-4" data-testid="cart-checkout">Finalizar encomenda <ArrowRight size={14} /></Link>
          </div>
        </div>
      )}
    </div>
  );
}

export function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [addr, setAddr] = useState({
    name: user?.name || "", line1: "", line2: "", postal_code: "", city: "",
    country: "Portugal", phone: user?.phone || "", vat_number: user?.company?.vat_number || "",
  });
  const [shippingMethod, setShippingMethod] = useState("ctt_normal");
  const [paymentMethod, setPaymentMethod] = useState("mbway");
  const [notes, setNotes] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const shippingCost = { local_pickup: 0, ctt_normal: 5.90, ctt_express: 12.90 }[shippingMethod];
  const vat = Math.round(subtotal * 0.23 * 100) / 100;
  const total = subtotal + shippingCost + vat;

  if (!user) return (
    <div className="container-x py-14">
      <h1 className="text-3xl font-bold">Precisa de conta</h1>
      <p className="mt-2 text-muted-foreground">Para finalizar a encomenda, entre ou crie conta.</p>
      <Link to="/login" state={{ from: "/checkout" }} className="btn-primary mt-6">Entrar</Link>
    </div>
  );
  if (items.length === 0) return <div className="container-x py-14"><p className="tech-label">carrinho vazio</p><Link to="/loja" className="btn-primary mt-6">Ver produtos</Link></div>;

  async function submit(e) {
    e.preventDefault();
    if (!consent) return toast.error("Aceite os termos");
    setLoading(true);
    try {
      const payload = {
        lines: items.map((it) => ({
          product_id: it.product_id, name: it.name, unit_price: it.unit_price,
          quantity: it.quantity, variant: it.variant, customizations: it.customizations, notes: null,
        })),
        shipping_address: addr,
        shipping_method: shippingMethod,
        payment_method: paymentMethod,
        customer_notes: notes,
        consent_terms: true,
      };
      const { data } = await api.post("/orders", payload);
      clear();
      toast.success(`Encomenda ${data.code} criada`);
      nav(`/portal/encomendas/${data.order_id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container-x py-14 anim-in">
      <p className="tech-label mb-3">Checkout</p>
      <h1 className="text-4xl font-bold tracking-tighter">Finalizar encomenda</h1>
      <form onSubmit={submit} className="mt-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <section className="border border-border p-6 bg-card">
            <p className="tech-label mb-4">Morada de envio</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <input required placeholder="Nome completo" className="input" value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} data-testid="checkout-name" />
              <input required placeholder="Telefone" className="input" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} data-testid="checkout-phone" />
              <input required placeholder="Morada" className="input sm:col-span-2" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} data-testid="checkout-line1" />
              <input placeholder="Morada (linha 2)" className="input sm:col-span-2" value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} />
              <input required placeholder="Código-postal" className="input" value={addr.postal_code} onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })} data-testid="checkout-postal" />
              <input required placeholder="Localidade" className="input" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="checkout-city" />
              <input placeholder="NIF (opcional)" className="input" value={addr.vat_number} onChange={(e) => setAddr({ ...addr, vat_number: e.target.value })} />
            </div>
          </section>

          <section className="border border-border p-6 bg-card">
            <p className="tech-label mb-4">Envio</p>
            <div className="space-y-2">
              {[
                ["local_pickup", "Recolha em Barcelos", "0,00 €"],
                ["ctt_normal", "CTT Portugal continental (2-3 dias)", "5,90 €"],
                ["ctt_express", "CTT Expresso (24h)", "12,90 €"],
              ].map(([id, label, cost]) => (
                <label key={id} className={`flex items-center justify-between border p-3 cursor-pointer ${shippingMethod === id ? "border-brand bg-brand/5" : "border-border"}`}>
                  <div className="flex items-center gap-3"><input type="radio" checked={shippingMethod === id} onChange={() => setShippingMethod(id)} data-testid={`checkout-ship-${id}`} /> <span className="text-sm">{label}</span></div>
                  <span className="font-medium text-sm">{cost}</span>
                </label>
              ))}
            </div>
          </section>

          <section className="border border-border p-6 bg-card">
            <p className="tech-label mb-4">Pagamento</p>
            <div className="space-y-2">
              {[
                ["mbway", "MB WAY · +351 912 000 000"],
                ["bank_transfer", "Transferência bancária"],
                ["after_quote", "Aguardar confirmação por email"],
              ].map(([id, label]) => (
                <label key={id} className={`flex items-center gap-3 border p-3 cursor-pointer ${paymentMethod === id ? "border-brand bg-brand/5" : "border-border"}`}>
                  <input type="radio" checked={paymentMethod === id} onChange={() => setPaymentMethod(id)} data-testid={`checkout-pay-${id}`} />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
            <p className="mono text-[10px] mt-3 text-muted-foreground uppercase tracking-widest">Após submeter, receberá instruções detalhadas para efetuar o pagamento.</p>
          </section>

          <section className="border border-border p-6 bg-card">
            <p className="tech-label mb-2">Observações</p>
            <textarea className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notas para a produção…" data-testid="checkout-notes" />
          </section>
        </div>

        <aside className="border border-border p-6 h-fit sticky top-20 bg-card space-y-2">
          <p className="tech-label mb-3">Resumo</p>
          {items.map((it) => (
            <div key={it._key} className="flex justify-between text-sm py-1">
              <span className="truncate pr-2">{it.name} × {it.quantity}</span>
              <span className="font-medium">{formatEUR(it.unit_price * it.quantity)}</span>
            </div>
          ))}
          <div className="border-t border-border pt-3 mt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{formatEUR(subtotal)}</span></div>
            <div className="flex justify-between"><span>Portes</span><span>{formatEUR(shippingCost)}</span></div>
            <div className="flex justify-between"><span>IVA (23%)</span><span>{formatEUR(vat)}</span></div>
            <div className="flex justify-between font-bold text-lg pt-2 border-t border-border mt-2"><span>Total</span><span>{formatEUR(total)}</span></div>
          </div>
          <label className="flex items-start gap-2 mt-4 text-xs cursor-pointer">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="checkout-consent" />
            <span>Aceito os termos e condições e a política de privacidade.</span>
          </label>
          <button disabled={loading} className="btn-primary w-full justify-center mt-4" data-testid="checkout-submit">
            {loading ? "…" : "Confirmar encomenda"} <ArrowRight size={14} />
          </button>
        </aside>
      </form>
      <style>{`.input{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));padding:.6rem .75rem;font-size:.875rem;outline:none}.input:focus{border-color:var(--brand)}`}</style>
    </div>
  );
}
